<template>
  <div
    v-if="conversationId && $auth.isAuthenticated && getReady"
    class="advanced-conversation-detail"
    :class="{ 'optimized-layout': !isDesktop }"
  >
    <div class="advanced-conversation-detail--chat">
      <ConversationChatContainer :conversationId="conversationId" />
    </div>
    <div v-if="!isDesktop" class="advanced-conversation-detail--mobile-btns">
      <router-link to="/">
        <b-button variant="info" pill>
          <b-icon-arrow-bar-left />
        </b-button>
      </router-link>
      <b-button variant="info" pill @click="toggleConverationInfo">
        <b-icon-info-lg v-if="!isConversationInfoOpened" />
        <b-icon-x-lg v-else />
      </b-button>
    </div>
    <div
      class="advanced-conversation-detail--info"
      :class="{
        opened: conversationInfoOpened,
        'bg-light': !isDesktop
      }"
    >
      <div class="advanced-conversation-detail--info-tabs mb-3 border bg-white-custom">
        <div class="advanced-conversation-detail--info-tabs--header d-flex align-item-center border-bottom">
          <div
            class="tab-item border border-start-0 border-bottom-0 border-top-0"
            :class="{
              'active': tabIndex === 0,
              'dark': isDarkMode,
            }"
            @click="() => { tabIndex = 0; }"
          >
            Call
          </div>
          <div
            class="tab-item border border-start-0 border-bottom-0 border-top-0"
            :class="{
              'active': tabIndex === 1,
              'dark': isDarkMode,
            }"
            @click="() => { tabIndex = 1; }"
          >
            Summary
            <b-icon
              v-if="hasData.summary"
              icon="dot"
              variant="success"
              scale="3"
             style="margin-top: -1rem; margin-left: -4px;"
            />
          </div>
          <div
            class="tab-item border border-start-0 border-bottom-0 border-top-0"
            :class="{
              'active': tabIndex === 2,
              'dark': isDarkMode,
            }"
            @click="() => { tabIndex = 2; }"
          >
            Keywords
            <b-icon
              v-if="hasData.keywords"
              icon="dot"
              variant="success"
              scale="3"
             style="margin-top: -1rem; margin-left: -4px;"
            />
          </div>
          <div
            class="tab-item"
            :class="{
              'active': tabIndex === 3,
              'dark': isDarkMode,
            }"
            @click="() => { tabIndex = 3; }"
          >
            Sentiment
            <b-icon
              v-if="hasData.sentiment"
              icon="dot"
              variant="success"
              scale="3"
             style="margin-top: -1rem; margin-left: -4px;"
            />
          </div>
        </div>
        <div class="advanced-conversation-detail--info-tabs--content bg-white-custom">
          <ConversationInfo v-show="tabIndex == 0" :conversationId="conversationId" :currentRecipientLang="currentRecipientLang" />
          <ConversationSummary
            v-show="tabIndex == 1"
            :conversationId="conversationId" :conversation="currentConversation"
            @updated="() => {
              if (!hasData.summary) {
                tabIndex = 1;
              }
              hasData.summary = true;
            }"
          />
          <ConversationKeywords
            v-show="tabIndex == 2"
            :conversationId="conversationId"
            @updated="() => { hasData.keywords = true; }"
          />
          <ConversationSentiment
            v-show="tabIndex == 3"
            :conversationId="conversationId"
            @updated="() => { hasData.sentiment = true; }"
          />
        </div>
      </div>
      <ConversationActions :conversationId="conversationId" :currentRecipientLang="currentRecipientLang" />
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters, mapActions } from "vuex";
import ConversationInfo from "./conversations/ConversationInfo";
import ConversationActions from "./conversations/ConversationActions";
import ConversationSentiment from "./conversations/ConversationSentiment";
import ConversationKeywords from "./conversations/ConversationKeywords";
import ConversationSummary from "./conversations/ConversationSummary";
import ConversationChatContainer from "./conversations/ConversationChatContainer";
import SocketHolder from "../SocketHolder";

export default {
  name: "AdvancedConversationDetail",
  components: {
    ConversationInfo,
    ConversationChatContainer,
    ConversationActions,
    ConversationSentiment,
    ConversationKeywords,
    ConversationSummary,
  },
  computed: {
    ...mapGetters("conversations", ["currentConversation", "conversationAttachments"]),
    ...mapGetters("agency", ["currentAgency", "agencySettings"]),
    ...mapGetters("user", ["currentToken", "viewport", "currentUser", "isDarkMode"]),
    ...mapGetters("video", ["videoRooms"]),
    ...mapGetters("sentiment", ["sentimentScores"]),
    internationalDialCodeEnabled() {
        return this.agencySettings.enableInternational == "true";
    },
    conversationId() {
      return this.$route.params.conversationId;
    },
    conversationVideoRoom() {
      if (this.videoRooms && this.videoRooms.length > 0) {
        return this.videoRooms.find(room => room.conversation_id == this.conversationId);
      }

      return null;
    },
    getReady() {
      return this.hasAccess && !this.loadingConvo && !this.loadingAttachment && !this.loadingGeography && !this.loadingVideoRooms;
    },
    conversationInfoOpened() {
      if (this.isDesktop) {
        return true;
      } else {
        return this.isConversationInfoOpened;
      }
    },
    conversationSentimentScores() {
      return this.sentimentScores[this.$route.params.conversationId]
        ? this.sentimentScores[this.$route.params.conversationId].scores
        : [];
    },
  },
  data() {
    return {
      currentNode: "Conversation",
      pageTitle: null,
      currentRecipientLang: "",
      error: null,
      isDesktop: true,
      loadingConvo: true,
      loadingAttachment: true,
      loadingGeography: true,
      loadingVideoRooms: true,
      isConversationInfoOpened: false,
      tabIndex: 0, // 0: Call, 1: Summary, 2: Keywords, 3: Sentiment
      hasData: {
        summary: false,
        keywords: false,
        sentiment: false,
      },
      fetchAbortController: null,
      hasAccess: true,
    };
  },
  created() {
    if (this.currentAgency) {
      this.tabIndex = 0;
      this.fetchConversationInfo();
    }
  },
  methods: {
    ...mapActions("conversations", ["loadConversation", "loadAttachments"]),
    fetchConversationInfo() {
      this.fetchAbortController = new AbortController();
      this.fetchConversation(this.conversationId);
      this.fetchGeography(this.conversationId);
      this.fetchAttachments(this.conversationId);
      this.fetchVideoRooms(this.conversationId);
    },
    async fetchConversation(conversationId) {
      await this.$ConveyApi.conversationsApi.getConversation({
        conversationId,
        signal: this.fetchAbortController.signal,
      })
        .then(res => {
          const { status: conversationStatus } = res.data.data;
          this.loadingConvo = false;
          if (conversationStatus !== "archived") {
            this.loadConversation(res.data.data);
          } else {
            this.hasAccess = false;
            this.abortFetchOperations();
            if (this.$auth.can('admin:agency')) {
              if (confirm(`The given conversation is ${conversationStatus}.\nWill you check the conversation in Admin Conversations?`)) {
                this.$router.push({ path: `/admin/conversations/${conversationId}` }).catch(() => {});
              } else {
                this.$router.push({ name: "Conversations Dashboard" }).catch(() => {});
              }
            } else {
              if (confirm(`The given conversation is ${conversationStatus}.\nYou don't have access to check the conversation`)) {
                this.$router.push({ name: "Conversations Dashboard" }).catch(() => {});
              } else {
                this.$router.push({ name: "Conversations Dashboard" }).catch(() => {});
              }
            }
          }
        }).catch(err => {
          console.log(err);
          this.loadingConvo = false;
          this.hasAccess = false;
        });

      this.pageTitle = this.prettyPhoneNumber(
        this.currentConversation.primary_recipient
      );

      if (this.currentConversation.operator_id == this.currentUser.user_id)
        SocketHolder.socket.emit("readConversation", this.currentAgency.agency_id, this.conversationId);
    },
    async fetchVideoRooms(conversationId) {
      this.loadingVideoRooms = true;

      await this.$ConveyApi.videoApi.getVideoRooms({
        params: {
          conversationId,
          status: 'active',
        },
        signal: this.fetchAbortController.signal,
      }).then(res => {
        const rooms = res.data.data;
        if (rooms && rooms.length > 0)
          this.$store.commit("video/upsertVideoRoom", rooms[0]);
        this.loadingVideoRooms = false;
      }).catch(err => {
        console.log(err);
        this.loadingVideoRooms = false;
      });
    },
    async fetchAttachments(conversationId) {
      this.loadingAttachment = true;

      await this.$ConveyApi.attachmentsApi.getAttachments({
        params: { conversationId },
        signal: this.fetchAbortController.signal,
      }).then(res => {
        this.loadAttachments(res.data.data);
        this.loadingAttachment = false;
      }).catch(err => {
        console.log(err);
        this.loadingAttachment = false;
      });
    },
    async fetchGeography(conversationId) {
      this.loadingGeography = true;
      if (this.currentConversation.conversation_id != conversationId) {
        this.loadingGeography = false;
        return;
      }

      const recipId = this.currentConversation.recipients[0].recipient_id;
      await this.$ConveyApi.recipientsApi.getRecipientGeolocations({
        numberOrId: recipId,
        limit: 1,
        signal: this.fetchAbortController.signal,
      }).then(res => {
        this.$store.commit("conversations/updateGeography", res.data.data);
        this.loadingGeography = false;
      }).catch(err => {
        console.log(err);
        this.loadingGeography = false;
      });
    },
    prettyPhoneNumber(inPh) {
      let outPh;
      try {
        if (inPh.length == 10) {  //support for older phone number saved in db
          inPh = "1" + inPh;
        }
        if (!inPh.startsWith("+")) {  //support for older phone number saved in db
          inPh = "+" + inPh;
        }
        if (this.internationalDialCodeEnabled) {
          outPh = inPh.replace(/(\d{0,3})(\d\d\d)(\d\d\d)(\d\d\d\d)/, "$1-$2-$3-$4");
        }
        else {
          outPh = inPh.substring(2).replace(/(\d\d\d)(\d\d\d)(\d\d\d\d)/, "$1-$2-$3");
        }
      } catch (error) {
        outPh = inPh;
      }
      return outPh;
    },
    toggleConverationInfo() {
      this.isConversationInfoOpened = !this.isConversationInfoOpened;
    },
    abortFetchOperations() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    }
  },
  watch: {
    currentAgency: function (agency) {
      if (agency && agency.agency_id) {
        this.fetchConversationInfo();
      }
    },
    "$route.params": {
        handler(path) {
          if (path === "/admin/templates")
            this.fetchTemplates();
        },
        deep: true,
        immediate: true
    },
    currentConversation: {
      handler(conversation) {
        if (conversation && conversation.recipients && conversation.recipients.length > 0) {
          const { primary_recipient } = conversation;
          const primaryRecipient = conversation.recipients.find(r => r.number === primary_recipient);
          if (primaryRecipient) {
            this.currentRecipientLang = primaryRecipient.language;
          }
        }
      },
      deep: true,
      immediate: true,
    },
    viewport: {
      handler(value) {
        const { innerWidth } = value;
        this.isDesktop = innerWidth > 1199;
      },
      deep: true,
      immediate: true,
    },
  },
  beforeDestroy() {
    this.abortFetchOperations();
  }
};
</script>

<style lang="scss">
.advanced-conversation-detail {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr min(600px, max(400px, 33%));
  gap: 1rem;

  &--info {
    opacity: 0;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    transition: opacity ease-in-out 0.15s;

    &.opened {
      opacity: 1;
    }

    &-tabs {
      border-radius: 1rem;
      &--header {
        width: 100%;
        height: 40px;
        border-radius: 1rem 1rem 0 0;
        .tab-item {
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          width: 25%;
          height: 100%;
          padding: 8px;
          font-weight: 500;
          opacity: 1;
          &:first-child {
            border-radius: 1rem 0 0 !important;
          }
          &:last-child {
            border-radius: 0 1rem 0  0 !important;
          }
          &.active {
            background-color: #e9edf2 !important;
            &.dark {
              background-color: #545b62 !important;
            }
          }
          &:hover {
            opacity: 0.7;
            background-color: #e9edf2 !important;
            &.dark {
              background-color: #545b62 !important;
            }
          }
          @media (max-width: 400px) {
            font-size: 13px;
          }
          @media (max-width: 375px) {
            font-size: 11px;
          }
        }
      }

      &--content {
        border-radius: 0 0 1rem 1rem;
      }
    }
  }

  &--chat {
    height: 100%;
    overflow-y: auto;
  }

  &.optimized-layout {
    position: relative;
    display: block;

    .advanced-conversation-detail--chat {
      position: relative;
      z-index: 2;
    }

    .advanced-conversation-detail--info {
      position: absolute;
      top: 0;
      right: 0;
      z-index: 1;

      &.opened {
        z-index: 3;
      }
    }

    .advanced-conversation-detail--mobile-btns {
      position: absolute;
      z-index: 4;
      top: 16px;
      right: 16px;
      display: flex;
      align-items: center;
      gap: 8px;

      button {
        background-color: rgba(24, 109, 222, 0.5) !important;
      }
    }
  }
}
</style>